import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { store } from 'App';
import { navigate, pageDataService } from 'common';
import { performanceService } from 'common/utilities/PerformanceService';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import i18n from 'i18n';
import { alertIdQueryParamName, breachIdQueryParamName, csidQueryParamName, currentUserCsidQueryParamName } from 'RouteConstants';
import { creditMonitoringBasePath, creditMonitoringReportPath, identityBasePath, notificationSettingsRoute } from 'Routes';
import {
  IItpUserContext,
  IWebViewState,
  NativeDialogArea,
  resetAlerts,
  resetWebViewStates,
  restartPageLoad,
  RootState,
  SessionState,
  setDialogClosing,
  setIsPreviousDialog,
  setSessionState,
} from 'store';
import { getCreditSummaryAsync } from 'store/identityMonitoring/creditMonitoringSlice';
import {
  getBreachesAsync,
  getCompleteSubscriberProfileAsync,
  getFamilyITPSummaryAsync,
  resetBreachStateByCsid,
  resetFamilyMemberBreaches,
  setCurrentUserContext,
  setUserContext,
} from 'store/identityMonitoring/identityMonitoringSlice';

type DispatchType = ThunkDispatch<RootState, unknown, AnyAction>;

export function reloadFullPageIfNeeded() {
  const elapsedTime = performanceService.getElapsedTimeInMinutes();
  if (elapsedTime > 30) {
    TelemetryProvider.Instance.trackEvent('reloadFullPage', { elapsedTime, href: window.location.href });
    window.location.reload();
  }
}

function reloadWebViewDialogIfNeeded(path: string) {
  const elapsedTime = performanceService.getElapsedTimeInMinutes();
  if (elapsedTime > 30) {
    TelemetryProvider.Instance.trackEvent('reloadWebViewDialog', { path, elapsedTime });
    window.location.href = path;
  }
}

/**
 * Updates web view store by fetching REST APIs based on dialog area
 *
 * @param webViewState WebView state
 * @param userContext User context
 */
export const updateWebViewState = (webViewState: IWebViewState, userContext: IItpUserContext) => (dispatch: DispatchType) => {
  // i18next-extract-mark-ns-start identity
  const { nativeDialogArea } = webViewState;
  TelemetryProvider.Instance.trackEvent('updateWebViewState', { area: nativeDialogArea?.toString() || 'unknown' });
  const { csid, isCurrentUser } = userContext;
  switch (nativeDialogArea) {
    case NativeDialogArea.OnboardFamilyMember:
      dispatch(getFamilyITPSummaryAsync.forceRequest());
      break;

    case NativeDialogArea.RecommendationDialog:
    case NativeDialogArea.UndoDialog:
      dispatch(getBreachesAsync.forceRequest({ csid }));
      break;

    case NativeDialogArea.AddIdentity:
    case NativeDialogArea.RemoveIdentity:
    case NativeDialogArea.VerifyIdentity:
    case NativeDialogArea.BulkAdd:
      dispatch(getCompleteSubscriberProfileAsync.forceRequest(csid));
      dispatch(getBreachesAsync.forceRequest({ csid }));
      break;

    case NativeDialogArea.RemoveSubscription:
      dispatch(getFamilyITPSummaryAsync.forceRequest())
        .unwrap()
        .then((familyMembers) => {
          const isITPOnboarded = familyMembers.find((m) => localeIgnoreCaseEquals(m.csid, csid))?.itpOnboardingStatus === 'Monitoring';

          // check ITP subscription removal was successful and
          // show the success toast message only for success scenario
          if (!isITPOnboarded) {
            if (isCurrentUser) {
              window.nativeAppApi.goBack();
            }
            window.nativeAppApi.showToast(i18n.t('identity:RemoveSubscriptionDialog.Success'));
          }
        });

      break;

    case NativeDialogArea.CreditAlertDialog:
      dispatch(getCreditSummaryAsync.forceRequest(csid!));
      break;

    case NativeDialogArea.CreditReportDetails:
      // Ignore refresh request after CreditReportDetails is closed
      // as it doesn't change any state in credit report
      break;

    default:
      window.location.reload();
  }
};

/**
 * Refreshes page data w/o reloading the page
 *
 */
export const refreshData = () => (dispatch: DispatchType) => {
  reloadFullPageIfNeeded();

  const query = new URLSearchParams(window.location.search);
  const csidOverride = query.get(csidQueryParamName);
  const userData = pageDataService.getUserData();
  const csid = csidOverride || userData.csid;

  let path = window.location.pathname || '';
  if (path.endsWith('/')) {
    path = path.slice(0, -1); // trim '/'
  }

  if (localeIgnoreCaseEquals(path, identityBasePath)) {
    // eslint-disable-next-line no-console
    console.log(`Refreshing ITM data: ${window.location.href}`);
    dispatch(resetFamilyMemberBreaches());
    dispatch(getFamilyITPSummaryAsync.forceRequest());
    dispatch(getBreachesAsync.forceRequest({ csid }));
  } else if (localeIgnoreCaseEquals(path, creditMonitoringBasePath)) {
    // eslint-disable-next-line no-console
    console.log(`Refreshing CM data: ${window.location.href}`);
    dispatch(setSessionState(SessionState.Unknown));
    dispatch(getFamilyITPSummaryAsync.forceRequest());
    dispatch(getCreditSummaryAsync.forceRequest(csid!));
  } else if (localeIgnoreCaseEquals(path, creditMonitoringReportPath)) {
    // Workaround for Windows issue where mouse back button navigates to dashboard from credit report page
    // This issue will go away in the next release where refresh API will be replaced with navigateByPageUrl
    window.location.pathname = creditMonitoringBasePath;
  } else if (localeIgnoreCaseEquals(path, notificationSettingsRoute)) {
    // no-op since WebViewNotificationSettings will use webView.componentKey state to re-mount the panel,
    // which will trigger setting reload and handle deep link params
  } else {
    window.location.reload();
  }

  if (!pageDataService.isCurrentUser(csid)) {
    const { userContext } = store.getState().identityMonitoring;
    if (!localeIgnoreCaseEquals(userContext.csid, csid)) {
      dispatch(setUserContext({ csid, userId: undefined, isCurrentUser: false, displayName: '' }));
    }
  } else {
    dispatch(setCurrentUserContext());
  }
};

/**
 * Navigates to a page url via client-side routing.
 *
 * @param pageUrl a page url to navigate
 */
export const navigateWebViewPageUrl = (pageUrl: string) => (dispatch: DispatchType) => {
  const url = new URL(pageUrl);
  const path = url.pathname + url.search;

  // eslint-disable-next-line no-console
  console.log(`Navigating to ${path}`);
  navigate(path);

  dispatch(refreshData());

  // reset states after component is swapped
  dispatch(resetWebViewStates());
  dispatch(restartPageLoad());
};

/**
 * Navigates to a dialog url via client-side routing.
 *
 * @param dialogUrl dialog url to navigate
 */
export const navigateWebViewDialogUrl = (dialogUrl: string) => (dispatch: DispatchType) => {
  const url = new URL(dialogUrl);
  const path = url.pathname + url.search;
  const csidParam = url.searchParams.get(csidQueryParamName);
  const currentUserCsid = url.searchParams.get(currentUserCsidQueryParamName);
  const displayName = url.searchParams.get('name') || '';
  const csid = csidParam || currentUserCsid;
  const breachId = url.searchParams.get(breachIdQueryParamName);
  const alertId = url.searchParams.get(alertIdQueryParamName);

  reloadWebViewDialogIfNeeded(path);

  dispatch(setDialogClosing(false));

  // prevent rendering of a current dialog until component swap completes
  dispatch(setIsPreviousDialog());

  if (csid && breachId) {
    // reset breach state for recommendation dialog
    dispatch(resetBreachStateByCsid(csid));
  }

  if (alertId) {
    // reset alert for device alert dialog
    dispatch(resetAlerts());
  }

  dispatch(setSessionState(SessionState.Unknown));

  // eslint-disable-next-line no-console
  console.log(`Navigating to ${path}`);
  navigate(path);

  // TODO: remove name in the query param and use useSelectedFamilyMember hook
  const currentUserData = pageDataService.getUserData();
  if (csid) {
    const isCurrentUser = pageDataService.isCurrentUser(csid);
    const userId = isCurrentUser ? currentUserData.userId : undefined;
    const name = isCurrentUser ? currentUserData.displayName : displayName;
    dispatch(setUserContext({ csid, userId, isCurrentUser, displayName: name }));
  } else {
    dispatch(setCurrentUserContext());
  }

  // reset states after component is swapped
  dispatch(resetWebViewStates());
  dispatch(restartPageLoad());
};

// i18next-extract-mark-ns-stop identity
